// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: helpers shared by the Flows
// library, the conversation bubbles, the composer picker and the template
// senders (campaign step 2, direct send, new conversation). Shapes follow the
// cross-repo contract (content_attributes.kaption_flow,
// kaption_whatsapp_flow, processed_params.buttons[{type:'flow'}]).
import {
  formatFlowResponseLabel,
  formatFlowResponseValue,
} from 'dashboard/components-next/message/helpers/whatsappFlowResponse';

export const WHATSAPP_FLOWS_FLAG = 'KAPTION_WHATSAPP_FLOWS';

export const FLOW_STATUSES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  DEPRECATED: 'deprecated',
  THROTTLED: 'throttled',
  BLOCKED: 'blocked',
};

export const MASKED_VALUE = '•••';

// Tailwind classes per status pill (fork tokens).
const STATUS_TONES = {
  draft: 'bg-n-amber-3 text-n-amber-11',
  published: 'bg-n-teal-3 text-n-teal-11',
  deprecated: 'bg-n-slate-3 text-n-slate-11',
  throttled: 'bg-n-ruby-3 text-n-ruby-11',
  blocked: 'bg-n-ruby-3 text-n-ruby-11',
};

export const flowStatusTone = status =>
  STATUS_TONES[status] || STATUS_TONES.draft;

/**
 * A flow can be sent to customers when it is published. Throttled flows still
 * send (Meta caps them at 10 sends per hour). Drafts only in draft mode.
 */
export const isFlowSendable = (status, mode = 'published') => {
  if (mode === 'draft') return status !== FLOW_STATUSES.DEPRECATED;
  return [FLOW_STATUSES.PUBLISHED, FLOW_STATUSES.THROTTLED].includes(status);
};

export const isFlowBlockedForSending = status =>
  [FLOW_STATUSES.DEPRECATED, FLOW_STATUSES.BLOCKED].includes(status);

/** The FLOW button of a template, with its position, or null. */
export const getFlowButton = template => {
  const comp = template?.components?.find(c => c.type === 'BUTTONS');
  if (!comp || !Array.isArray(comp.buttons)) return null;
  const index = comp.buttons.findIndex(
    b => String(b.type || '').toUpperCase() === 'FLOW'
  );
  if (index < 0) return null;
  return { index, button: comp.buttons[index] };
};

export const templateHasFlowButton = template => !!getFlowButton(template);

/** Library flow a template's FLOW button points at (meta id, then name). */
export const findFlowForButton = (flows = [], button = {}) => {
  if (!button) return null;
  const metaId = button.flow_id ? String(button.flow_id) : null;
  return (
    flows.find(f => metaId && String(f.meta_flow_id) === metaId) ||
    flows.find(f => button.flow_name && f.name === button.flow_name) ||
    null
  );
};

const screensOf = flowJson =>
  Array.isArray(flowJson?.screens) ? flowJson.screens : [];

/**
 * Data keys a screen accepts (what `flow_action_data` can prefill). Defaults to
 * the button's navigate_screen, else the first screen.
 */
export const screenDataKeys = (flowJson, screenId = null) => {
  const screens = screensOf(flowJson);
  const screen =
    (screenId && screens.find(s => s.id === screenId)) || screens[0];
  if (!screen?.data || typeof screen.data !== 'object') return [];
  return Object.keys(screen.data);
};

/**
 * processed_params.buttons entry for a FLOW button. Empty prefill values are
 * dropped; with nothing to send the backend still auto-attaches a token.
 */
export const buildFlowButtonParam = ({
  index,
  kaptionFlowId = null,
  flowActionData = {},
}) => {
  const data = Object.fromEntries(
    Object.entries(flowActionData || {}).filter(
      ([, v]) => v !== undefined && v !== null && String(v).trim() !== ''
    )
  );
  const param = { index, type: 'flow' };
  if (kaptionFlowId) param.kaption_flow_id = kaptionFlowId;
  if (Object.keys(data).length) param.flow_action_data = data;
  return param;
};

/**
 * Put a flow entry into processed_params.buttons at the button's position
 * (the backend reads the array position, or an explicit `index`). Holes stay
 * null, which the sender skips. A null flowParam leaves the list unchanged.
 */
export const mergeFlowButtonParam = (buttons = [], flowParam = null) => {
  const next = Array.from(buttons || [], b => b ?? null);
  if (!flowParam) return next;
  while (next.length <= flowParam.index) next.push(null);
  next[flowParam.index] = flowParam;
  return next;
};

/**
 * How an answer reads to a person (mirrors Kaption::WhatsappFlows::AnswerDisplay):
 * choice ids become option titles from `field.options`, booleans (and OptIn
 * "true"/"false") become yes/no labels, lists join their titles. Ids without a
 * known option (dynamic data sources) stay as they are.
 */
export const formatFlowAnswer = (
  value,
  field = null,
  { yes = 'Yes', no = 'No' } = {}
) => {
  const options = field?.options || {};
  const title = item => {
    if (item && typeof item === 'object') {
      return item.file_name || JSON.stringify(item);
    }
    const key = String(item);
    return Object.prototype.hasOwnProperty.call(options, key)
      ? options[key]
      : key;
  };
  if (value === true || value === false) return value ? yes : no;
  if (field?.type === 'OptIn' && (value === 'true' || value === 'false')) {
    return value === 'true' ? yes : no;
  }
  if (Array.isArray(value)) {
    return value.length ? value.map(title).join(', ') : '—';
  }
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return formatFlowResponseValue(value);
  return title(value);
};

/**
 * Labelled answers of a flow response: labels from kaption_flow.labels when
 * the backend matched the flow, else upstream's generated label. Values read
 * by option title through kaption_flow.fields. Sensitive keys are masked
 * unless showSensitive. flow_token never shows.
 */
export const buildLabeledEntries = (
  responseJson,
  kaptionFlow = null,
  { showSensitive = false, booleanLabels = undefined } = {}
) => {
  const isObject =
    responseJson &&
    typeof responseJson === 'object' &&
    !Array.isArray(responseJson);
  const entries = isObject
    ? Object.entries(responseJson)
    : [['response', responseJson]];
  const labels = kaptionFlow?.labels || {};
  const fields = kaptionFlow?.fields || {};
  const sensitive = new Set(kaptionFlow?.sensitiveKeys || []);
  return entries
    .filter(([key]) => key !== 'flow_token')
    .map(([key, value]) => {
      const isSensitive = sensitive.has(key);
      return {
        key,
        label: labels[key] || formatFlowResponseLabel(key),
        value:
          isSensitive && !showSensitive
            ? MASKED_VALUE
            : formatFlowAnswer(value, fields[key], booleanLabels),
        sensitive: isSensitive,
      };
    });
};

/** Ready-to-paste MCP prompt for creating or editing a flow. */
export const buildMcpPrompt = ({ inboxName = '', flow = null } = {}) => {
  if (flow) {
    return `Using Rumo, edit the WhatsApp flow "${flow.name}" (id ${flow.id}) in the ${inboxName} inbox: `;
  }
  return `Using Rumo, create a WhatsApp flow for the ${inboxName} inbox to `;
};

/** Is the 24h customer-service window open for this conversation? */
export const canSendInChatFlow = conversation => !!conversation?.can_reply;

export const READINESS_CHECK_ORDER = [
  'connection',
  'flow_access',
  'flows_webhook',
  'encryption_key',
  'business_verification',
  'phone_quality',
];

const READINESS_STATES = ['ok', 'missing', 'warning'];

const normalizeState = value => {
  if (value === true) return 'ok';
  if (value === false) return 'missing';
  return READINESS_STATES.includes(value) ? value : 'warning';
};

/**
 * Readiness payload → ordered [{key, state, detail}]. Accepts
 * `{checks: [{key, state, detail}]}` or a `{key: state | {state, detail}}` map.
 */
export const normalizeReadinessChecks = payload => {
  let list = [];
  if (Array.isArray(payload?.checks)) {
    list = payload.checks.map(c => ({
      key: c.key,
      state: normalizeState(c.state ?? c.ok),
      detail: c.detail || c.message || '',
      ...(c.secondary && { secondary: c.secondary }),
    }));
  } else if (payload && typeof payload === 'object') {
    list = Object.entries(payload.checks || payload).map(([key, v]) => ({
      key,
      state: normalizeState(v && typeof v === 'object' ? (v.state ?? v.ok) : v),
      detail: (v && typeof v === 'object' && (v.detail || v.message)) || '',
    }));
  }
  const rank = key => {
    const i = READINESS_CHECK_ORDER.indexOf(key);
    return i < 0 ? READINESS_CHECK_ORDER.length : i;
  };
  return list.filter(c => c.key).sort((a, b) => rank(a.key) - rank(b.key));
};

/** response_json with sensitive values replaced by MASKED_VALUE. */
export const maskSensitiveAnswers = (responseJson, kaptionFlow = null) => {
  const sensitive = kaptionFlow?.sensitiveKeys || [];
  if (
    !sensitive.length ||
    !responseJson ||
    typeof responseJson !== 'object' ||
    Array.isArray(responseJson)
  ) {
    return responseJson;
  }
  return Object.fromEntries(
    Object.entries(responseJson).map(([key, value]) => [
      key,
      sensitive.includes(key) ? MASKED_VALUE : value,
    ])
  );
};

/**
 * processed_params with the template's FLOW button entry added (or replaced).
 * Templates without a FLOW button come back unchanged. Kept out of
 * buildTemplateParameters on purpose: upstream's parser treats every button
 * entry as a required text parameter.
 */
export const applyFlowButtonParam = (
  template,
  processedParams = {},
  { flowActionData = {}, kaptionFlowId = null } = {}
) => {
  const flowButton = getFlowButton(template);
  if (!flowButton) return processedParams;
  const param = buildFlowButtonParam({
    index: flowButton.index,
    kaptionFlowId,
    flowActionData,
  });
  return {
    ...processedParams,
    buttons: mergeFlowButtonParam(processedParams?.buttons, param),
  };
};

// ── Template builder FLOW button (whatsapp-flows.TEMPLATES.1) ──

/** Screen ids of a Flow JSON, in order. */
export const flowScreenIds = flowJson =>
  screensOf(flowJson)
    .map(s => s?.id)
    .filter(Boolean);

/**
 * Why a library flow can't back a template button (i18n key suffix), or null
 * when it can. Templates need a published (or throttled) flow.
 */
export const flowTemplateUnavailableReason = status => {
  if (isFlowSendable(status)) return null;
  if (status === FLOW_STATUSES.DEPRECATED) return 'DEPRECATED';
  if (status === FLOW_STATUSES.BLOCKED) return 'BLOCKED';
  return 'DRAFT';
};

/**
 * whatsapp_templates create payload for a FLOW button. The backend resolves
 * kaption_flow_id to the Meta flow id and defaults navigate_screen to the
 * flow's first screen.
 */
export const buildTemplateFlowButtonPayload = button => ({
  type: 'FLOW',
  text: button.text,
  kaption_flow_id: button.kaption_flow_id || undefined,
  flow_action: 'navigate',
  navigate_screen: button.navigate_screen || undefined,
});

// whatsapp-flows 0.11: embed=1 tells wapreview's /show?mode=flow it lives
// inside the dashboard, so it hides the simulator's close (X) button, which
// closes nothing here.
export const withEmbedParam = rawUrl => {
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl);
    parsed.searchParams.set('embed', '1');
    return parsed.toString();
  } catch {
    return rawUrl;
  }
};

/**
 * Input names of a Flow JSON in the order a person meets them: screens in
 * order, then components depth-first within each screen. Used to order the
 * field_map, whose keys come back sorted by Postgres (jsonb), not by screen.
 */
export const flowFieldOrder = flowJson => {
  const names = [];
  const walk = node => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object') return;
    if (
      typeof node.name === 'string' &&
      node.type &&
      !names.includes(node.name)
    ) {
      names.push(node.name);
    }
    Object.entries(node).forEach(([key, value]) => {
      if (key !== 'data') walk(value);
    });
  };
  screensOf(flowJson).forEach(screen => walk(screen?.layout));
  return names;
};

/** [{key, ...field}] of a field_map in Flow JSON order; unknown keys last. */
export const orderedFlowFields = (fieldMap = {}, ...flowJsons) => {
  const order = [];
  flowJsons.forEach(json =>
    flowFieldOrder(json).forEach(name => {
      if (!order.includes(name)) order.push(name);
    })
  );
  const rank = key => {
    const i = order.indexOf(key);
    return i < 0 ? order.length : i;
  };
  return Object.entries(fieldMap || {})
    .map(([key, field], index) => ({ key, ...field, index }))
    .sort((a, b) => rank(a.key) - rank(b.key) || a.index - b.index)
    .map(({ index, ...field }) => field);
};
